import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { useId } from 'react'

import { Checkbox } from '@/components/checkbox/checkbox'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { updateCaseExtraInfoMutation } from '@/lib/queries/business/v1/case-extra/mutations'
import { getCaseExtraInfoQueryKey } from '@/lib/queries/business/v3/case-extra/queries'
import { cn } from '@/lib/utils'
import { doPolling } from '@/lib/utils/function'

export interface MarkPremiumCaseProps {
  caseId: number
  markedPremium?: boolean
}

export const MarkPremiumCase = ({ caseId, markedPremium = false }: MarkPremiumCaseProps) => {
  const checkboxId = useId()

  const { mutateAsync: updateCaseExtraInfo, isPending: isUpdatingCaseExtraInfo } =
    useMutation(updateCaseExtraInfoMutation)

  const handleCheckedChange = async (checked: boolean) => {
    await updateCaseExtraInfo(
      { caseId, isPremium: checked },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getCaseExtraInfoQueryKey(caseId),
          })

          doPolling({
            fn: () => {
              void queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.business.v4.case.search.index],
              })
            },
            times: 4,
            interval: 2000,
          })
        },
      }
    )
  }

  return (
    <div
      className={cn('flex items-center', 'mt-3', {
        'opacity-50': isUpdatingCaseExtraInfo,
      })}
    >
      <Checkbox
        id={checkboxId}
        className={cn('border-border data-[state=checked]:border-primary')}
        checked={markedPremium ?? false}
        loading={isUpdatingCaseExtraInfo}
        onCheckedChange={handleCheckedChange}
      />
      <label htmlFor={checkboxId} className={cn('text-xs font-medium text-slate-500', 'ml-2')}>
        Is this a Premium Processing Case?
      </label>
    </div>
  )
}
